<template>
  <div ref="containerRef" class="tw-relative fast-default-banner">
    <AnimationBanner
      ref="bannerRef"
      v-bind="$attrs"
      :model-value="modelValue"
      :navigation="false"
      :swipeable="modelValue.length > 1"
      :arrows="modelValue.length > 1"
      prev-icon="arrow_back_ios_new"
      next-icon="arrow_forward_ios"
    />
    <!-- Custom Navigation Dots -->
    <div v-if="modelValue.length > 1" class="banner-custom-dots">
      <button
        v-for="(banner, index) in modelValue"
        :key="banner._id"
        :aria-label="`Go to slide ${index + 1}`"
        class="banner-dot"
        :class="{ 'banner-dot--active': bannerRef?.activeSlide === banner._id }"
        @click="bannerRef?.goToSlide(banner._id)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
defineProps({
  modelValue: {
    type: Array as PropType<Banner[]>,
    required: true,
  },
})

const bannerRef = ref()
const containerRef = ref<HTMLElement>()

onMounted(() => {
  const container = containerRef.value
  if (!container) return
  const arrows = container.querySelectorAll('.q-carousel__arrow .q-btn')
  arrows.forEach((btn) => {
    const icon = btn.querySelector('.q-icon')
    if (icon?.textContent?.includes('back')) {
      btn.setAttribute('aria-label', 'Previous slide')
    } else if (icon?.textContent?.includes('forward')) {
      btn.setAttribute('aria-label', 'Next slide')
    }
  })
})
</script>

<style>
/* Mobile / tablet: full-width square (1:1) */
.fast-default-banner {
  height: 100vw;
}

/* Desktop: match the backoffice crop boundary (1920x822). */
@media (min-width: 768px) {
  .fast-default-banner {
    height: calc(100vw * 822 / 1920);
    max-height: 822px;
    margin-top: 0;
  }
}

/* Stretch the carousel to fill the container absolutely so we bypass
   Quasar's default height:400px and the base banner-carousel max-height cap.
   fast-default-banner already has position:relative (tw-relative). */
.fast-default-banner .q-carousel {
  position: absolute !important;
  inset: 0 !important;
  height: auto !important;
  max-height: none !important;
}

/* All inner Quasar panels must also fill the now-absolutely-sized carousel. */
.fast-default-banner .q-panel-parent,
.fast-default-banner .q-carousel__slides-container,
.fast-default-banner .q-panel {
  height: 100% !important;
  min-height: 0 !important;
}

.fast-default-banner .q-carousel__slide {
  height: 100% !important;
  min-height: 0 !important;
  padding: 0;
}

.fast-default-banner .q-carousel__slide img {
  height: 100% !important;
  width: 100% !important;
  object-fit: cover;
}

.banner-custom-dots {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: 10;
}

.banner-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  padding: 0;
  cursor: pointer;
  background-color: rgba(255, 255, 255, 0.3);
  transition: background-color 0.3s ease;
}

.banner-dot--active {
  background-color: rgba(255, 255, 255, 1);
}

.banner-carousel .q-carousel__arrow .q-btn {
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  margin-left: 32px;
  margin-right: 32px;
  border-radius: 50%;
  background-color: transparent !important;
  color: rgba(255, 255, 255, 0.5) !important;
  box-shadow: none !important;
  transition: color 0.3s ease;
}

.banner-carousel .q-carousel__arrow .q-btn:hover {
  color: rgba(255, 255, 255, 1) !important;
}

.banner-carousel .q-carousel__arrow .q-btn::before {
  display: none !important;
}

.banner-carousel .q-carousel__arrow .q-btn .q-focus-helper,
.banner-carousel .q-carousel__arrow .q-btn .q-ripple {
  display: none !important;
}

.banner-carousel .q-carousel__arrow .q-btn:active,
.banner-carousel .q-carousel__arrow .q-btn:focus {
  background-color: transparent !important;
}

.banner-carousel .q-carousel__arrow .q-btn .q-icon {
  font-size: 36px;
  -webkit-text-stroke: 2px currentColor;
}

@media (max-width: 767px) {
  .banner-custom-dots {
    bottom: 16px;
  }

  .banner-carousel .q-carousel__arrow--left {
    left: 16px !important;
  }

  .banner-carousel .q-carousel__arrow--right {
    right: 16px !important;
  }

  .banner-carousel .q-carousel__arrow .q-btn {
    width: 20px;
    height: 20px;
    min-width: 20px;
    min-height: 20px;
    margin-left: 0;
    margin-right: 0;
    padding: 0;
  }

  .banner-carousel .q-carousel__arrow .q-btn .q-icon {
    font-size: 20px;
    -webkit-text-stroke: 1px currentColor;
  }
}
</style>
